import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  PlusIcon,
  UserGroupIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { crewApi, formatLocalDateTimeInput, todayDateInput } from "../../../api/crew";
import { PlanningQuickViewTabs } from "../../../components/dashboard/calendar/PlanningQuickViewTabs";

/* ──────────────────────────────────────────────────────────────────────────
 * Design tokens — slate palette aligned with the dashboard report page.
 * Scoped to this page via the .crew-scope wrapper so we don't leak globally.
 * ────────────────────────────────────────────────────────────────────────── */
const SCOPE_STYLE = `
  .crew-scope {
    --crew-bg: #F8FAFC;
    --crew-paper: #FFFFFF;
    --crew-paper-2: #F8FAFC;
    --crew-ink: #020617;
    --crew-ink-2: #334155;
    --crew-ink-3: #64748B;
    --crew-ink-4: #94A3B8;
    --crew-line: #E2E8F0;
    --crew-line-2: #EEF2F7;
    --crew-sand: #F1F5F9;
    --crew-sand-2: #E2E8F0;
    --crew-terracotta: #0F172A;
    --crew-terracotta-soft: #E2E8F0;
    --crew-terracotta-ink: #0F172A;
    --crew-apricot: #3B82F6;
    --crew-sage: #2563EB;
    --crew-sage-soft: #DBEAFE;
    --crew-deep: #0F172A;
    --crew-warn: #F59E0B;
    --crew-rose: #DC2626;
    --crew-shadow: 0 1px 2px rgba(15,23,42,.04), 0 12px 32px -18px rgba(15,23,42,.28);
    color: var(--crew-ink);
    font-feature-settings: 'ss01', 'cv11';
    -webkit-font-smoothing: antialiased;
  }
  .crew-scope .crew-tnum { font-variant-numeric: tabular-nums; }
  .crew-scope .crew-mono { font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
  .crew-scope .crew-track::-webkit-scrollbar { height: 8px; }
  .crew-scope .crew-track::-webkit-scrollbar-thumb { background: var(--crew-sand-2); border-radius: 8px; }
`;

/* Person palette — used for avatars, bars, dots. Stable hash by id. */
const PERSON_PALETTE = [
  { color: "#E76F51", soft: "#FBE7DD", deep: "#B14A30" }, // terracotta
  { color: "#2A9D8F", soft: "#DEF2EE", deep: "#1F7A6F" }, // sage
  { color: "#F4A261", soft: "#FCEAD2", deep: "#B97732" }, // apricot
  { color: "#264653", soft: "#D6E1E5", deep: "#1A323C" }, // deep
  { color: "#8E7CC3", soft: "#E6E1F2", deep: "#5E4E94" }, // plum
  { color: "#E07A5F", soft: "#F8DBCF", deep: "#A94F38" }, // rose
];

const personColors = (key) => {
  const k = String(key || "0");
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) | 0;
  return PERSON_PALETTE[Math.abs(h) % PERSON_PALETTE.length];
};

const initialsFor = (label) => {
  if (!label) return "?";
  const s = String(label).trim();
  if (!s) return "?";
  const parts = s.split(/\s+/);
  if (parts.length === 1) return s.slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* ──────────────────────────────────────────────────────────────────────────
 * Form scaffolding (shared with original behavior)
 * ────────────────────────────────────────────────────────────────────────── */
const emptyShiftForm = () => {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setHours(start.getHours() + 6);
  return {
    title: "",
    location: "",
    role_label: "",
    starts_at: formatLocalDateTimeInput(start),
    ends_at: formatLocalDateTimeInput(end),
    break_minutes: 30,
    slots: 1,
    assignment_mode: "direct",
    status: "draft",
    leader_checklist_required: false,
    handover_note: "",
    project_id: "",
    project_activity_id: "",
    work_order_id: "",
    response_deadline_at: "",
  };
};

/* ──────────────────────────────────────────────────────────────────────────
 * Primitives — Card, Button, Pill, Segmented, Avatar
 * ────────────────────────────────────────────────────────────────────────── */
const Card = ({ children, className = "", ...props }) => (
  <div
    className={`rounded-2xl border bg-white ${className}`}
    style={{
      borderColor: "var(--crew-line)",
      boxShadow: "var(--crew-shadow)",
    }}
    {...props}
  >
    {children}
  </div>
);

const Button = ({ children, variant = "secondary", className = "", ...props }) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 transition";
  const styleByVariant = {
    primary: {
      background: "var(--crew-ink)",
      color: "#fff",
      border: "1px solid var(--crew-ink)",
    },
    accent: {
      background: "var(--crew-terracotta)",
      color: "#fff",
      border: "1px solid var(--crew-terracotta)",
    },
    danger: {
      background: "#fff",
      color: "var(--crew-rose)",
      border: "1px solid var(--crew-line)",
    },
    secondary: {
      background: "#fff",
      color: "var(--crew-ink-2)",
      border: "1px solid var(--crew-line)",
    },
  };
  return (
    <button
      type="button"
      className={`${base} ${className}`}
      style={styleByVariant[variant] || styleByVariant.secondary}
      {...props}
    >
      {children}
    </button>
  );
};

const Pill = ({ children, tone = "neutral" }) => {
  const tones = {
    neutral: { color: "var(--crew-ink-3)", background: "var(--crew-sand)" },
    terracotta: { color: "var(--crew-terracotta-ink)", background: "var(--crew-terracotta-soft)" },
    sage: { color: "#1F7A6F", background: "var(--crew-sage-soft)" },
    warn: { color: "#8A6800", background: "#FBEFC4" },
    rose: { color: "#A94F38", background: "#F8DBCF" },
  };
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={tones[tone] || tones.neutral}
    >
      {children}
    </span>
  );
};

const Segmented = ({ options, value, onChange, size = "md" }) => {
  const idx = options.findIndex((o) => (o.value ?? o) === value);
  const padY = size === "sm" ? "5px 12px" : "7px 16px";
  return (
    <div
      className="relative inline-flex"
      style={{
        background: "var(--crew-sand)",
        borderRadius: 999,
        padding: 3,
        fontSize: size === "sm" ? 12 : 13,
      }}
    >
      {idx >= 0 && (
        <div
          style={{
            position: "absolute",
            top: 3,
            bottom: 3,
            left: `calc(${(idx * 100) / options.length}% + 3px)`,
            width: `calc(${100 / options.length}% - 6px)`,
            background: "#fff",
            borderRadius: 999,
            boxShadow: "0 1px 3px rgba(40,30,20,.1)",
            transition: "left .25s cubic-bezier(.2,.7,.3,1)",
          }}
        />
      )}
      {options.map((o) => {
        const v = o.value ?? o;
        const lbl = o.label ?? o;
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            style={{
              position: "relative",
              zIndex: 1,
              border: "none",
              background: "transparent",
              padding: padY,
              cursor: "pointer",
              color: active ? "var(--crew-ink)" : "var(--crew-ink-3)",
              fontWeight: active ? 600 : 500,
              fontFamily: "inherit",
              transition: "color .15s",
            }}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
};

const Avatar = ({ label, size = 32, ring = false }) => {
  const palette = personColors(label);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: palette.soft,
        color: palette.deep,
        border: ring ? `2px solid ${palette.color}` : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 600,
        letterSpacing: "-0.02em",
        flexShrink: 0,
      }}
    >
      {initialsFor(label)}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * StretchyTimeBar — drag to move, drag handles to resize.
 * Times are absolute Date instances; positioning is relative to dayStart/dayEnd.
 * ────────────────────────────────────────────────────────────────────────── */
const fmtHM = (h) => {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const fmtDur = (a, b) => {
  const d = Math.max(0, b - a);
  const hours = Math.floor(d);
  const mins = Math.round((d - hours) * 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

function StretchyTimeBar({
  start,
  end,
  onChange,
  onSelect,
  dayStart = 6,
  dayEnd = 24,
  pxPerHour = 38,
  color = "var(--crew-terracotta)",
  soft = "var(--crew-terracotta-soft)",
  deep = "var(--crew-terracotta-ink)",
  label,
  sub,
  selected = false,
  height = 44,
  step = 0.25,
  readOnly = false,
}) {
  const [drag, setDrag] = useState(null);

  const left = (start - dayStart) * pxPerHour;
  const width = Math.max(8, (end - start) * pxPerHour);

  const snap = (h) => Math.round(h / step) * step;

  useEffect(() => {
    if (!drag || readOnly) return undefined;
    const onMove = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const dx = x - drag.startX;
      const dh = dx / pxPerHour;
      let s = drag.s0;
      let ee = drag.e0;
      if (drag.kind === "move") {
        const len = drag.e0 - drag.s0;
        s = snap(drag.s0 + dh);
        ee = s + len;
        if (s < dayStart) {
          s = dayStart;
          ee = s + len;
        }
        if (ee > dayEnd) {
          ee = dayEnd;
          s = ee - len;
        }
      } else if (drag.kind === "l") {
        s = Math.min(drag.e0 - 0.5, Math.max(dayStart, snap(drag.s0 + dh)));
      } else if (drag.kind === "r") {
        ee = Math.max(drag.s0 + 0.5, Math.min(dayEnd, snap(drag.e0 + dh)));
      }
      if (onChange) onChange(s, ee);
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [drag, pxPerHour, dayStart, dayEnd, onChange, step, readOnly]);

  const startDrag = (kind) => (e) => {
    if (readOnly) {
      if (onSelect) onSelect();
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    if (onSelect) onSelect();
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag({ kind, startX: x, s0: start, e0: end });
  };

  const showLabel = width > 56;
  const showSub = width > 100 && sub;
  const showDur = width > 130;

  return (
    <div
      onMouseDown={startDrag("move")}
      onTouchStart={startDrag("move")}
      style={{
        position: "absolute",
        left,
        width,
        height,
        top: "50%",
        transform: "translateY(-50%)",
        background: soft,
        border: `1.5px solid ${selected ? deep : color}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 10,
        cursor: readOnly ? "pointer" : drag?.kind === "move" ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
        boxShadow: selected
          ? `0 0 0 3px ${soft}, 0 6px 20px -8px ${color}`
          : "none",
        transition: drag ? "none" : "box-shadow .15s, border-color .15s",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 10px",
        color: deep,
        fontSize: 12,
        fontWeight: 600,
        zIndex: drag ? 5 : selected ? 3 : 1,
      }}
    >
      {!readOnly && (
        <div
          onMouseDown={startDrag("l")}
          onTouchStart={startDrag("l")}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: "ew-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: 2, height: height * 0.5, background: color, opacity: 0.55, borderRadius: 2 }} />
        </div>
      )}

      {showLabel && (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1, paddingLeft: 6, pointerEvents: "none", minWidth: 0 }}>
          <span style={{ fontWeight: 700, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
          </span>
          {showSub && (
            <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {sub}
            </span>
          )}
        </div>
      )}

      {showDur && (
        <span className="crew-mono" style={{ fontSize: 10.5, fontWeight: 500, opacity: 0.85, paddingRight: 6, pointerEvents: "none" }}>
          {fmtDur(start, end)}
        </span>
      )}

      {!readOnly && (
        <div
          onMouseDown={startDrag("r")}
          onTouchStart={startDrag("r")}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: "ew-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: 2, height: height * 0.5, background: color, opacity: 0.55, borderRadius: 2 }} />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * RangeTimeSlider — single track, two handles, hour ticks.
 * Replaces the two `<input type="time">` for shift start/end.
 * ────────────────────────────────────────────────────────────────────────── */
function RangeTimeSlider({
  start,
  end,
  onChange,
  min = 0,
  max = 24,
  step = 0.25,
  color = "var(--crew-terracotta)",
}) {
  const trackRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const span = max - min;
  const lp = ((start - min) / span) * 100;
  const rp = ((end - min) / span) * 100;

  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      let v = min + pct * span;
      v = Math.round(v / step) * step;
      if (drag === "l") onChange(Math.min(end - 0.5, Math.max(min, v)), end);
      if (drag === "r") onChange(start, Math.max(start + 0.5, Math.min(max, v)));
    };
    const up = () => setDrag(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [drag, start, end, min, max, span, step, onChange]);

  const ticks = [];
  for (let h = Math.ceil(min); h <= max; h += 2) {
    ticks.push(((h - min) / span) * 100);
  }

  return (
    <div style={{ width: "100%", userSelect: "none", touchAction: "none" }}>
      <div className="mb-2 flex items-end justify-between">
        <div>
          <div style={{ fontSize: 10, color: "var(--crew-ink-3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
            Start
          </div>
          <div className="crew-mono" style={{ fontSize: 22, fontWeight: 600, color: "var(--crew-ink)" }}>
            {fmtHM(start)}
          </div>
        </div>
        <div className="self-center text-center">
          <div className="crew-mono" style={{ fontSize: 12, color: "var(--crew-ink-3)" }}>
            {fmtDur(start, end)}
          </div>
        </div>
        <div className="text-right">
          <div style={{ fontSize: 10, color: "var(--crew-ink-3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
            End
          </div>
          <div className="crew-mono" style={{ fontSize: 22, fontWeight: 600, color: "var(--crew-ink)" }}>
            {fmtHM(end)}
          </div>
        </div>
      </div>
      <div
        ref={trackRef}
        style={{
          position: "relative",
          height: 44,
          background: "var(--crew-sand)",
          borderRadius: 22,
          boxShadow: "inset 0 1px 2px rgba(40,30,20,.06)",
        }}
      >
        {ticks.map((p, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${p}%`,
              top: 6,
              bottom: 6,
              width: 1,
              background: "rgba(40,30,20,.08)",
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            left: `${lp}%`,
            width: `${Math.max(0, rp - lp)}%`,
            top: 4,
            bottom: 4,
            background: color,
            borderRadius: 18,
            boxShadow: `0 4px 14px -4px ${color}`,
          }}
        />
        {[
          ["l", lp],
          ["r", rp],
        ].map(([k, p]) => (
          <div
            key={k}
            onMouseDown={(e) => {
              e.preventDefault();
              setDrag(k);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setDrag(k);
            }}
            style={{
              position: "absolute",
              left: `${p}%`,
              top: "50%",
              transform: "translate(-50%,-50%)",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#fff",
              border: `2.5px solid ${color}`,
              cursor: "grab",
              boxShadow: "0 2px 8px rgba(40,30,20,.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ width: 8, height: 2, background: color, borderRadius: 2 }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between" style={{ padding: "0 4px" }}>
        {[min, Math.round((min + max) / 2), max].map((h, i) => (
          <span key={i} className="crew-mono" style={{ fontSize: 10, color: "var(--crew-ink-4)" }}>
            {fmtHM(h)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * StatTile — minimal numeric callout
 * ────────────────────────────────────────────────────────────────────────── */
const StatTile = ({ icon: Icon, label, value, tone = "neutral" }) => {
  const tones = {
    neutral: { fg: "var(--crew-ink)", bg: "var(--crew-sand)", iconBg: "var(--crew-sand-2)", iconFg: "var(--crew-ink-2)" },
    terracotta: { fg: "var(--crew-terracotta-ink)", bg: "var(--crew-terracotta-soft)", iconBg: "#fff", iconFg: "var(--crew-terracotta)" },
    sage: { fg: "#1D4ED8", bg: "var(--crew-sage-soft)", iconBg: "#fff", iconFg: "var(--crew-sage)" },
    warn: { fg: "#92400E", bg: "#FEF3C7", iconBg: "#fff", iconFg: "#D97706" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{ background: t.iconBg, color: t.iconFg }}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p
            className="text-[10px] font-medium uppercase tracking-[.08em]"
            style={{ color: "var(--crew-ink-3)" }}
          >
            {label}
          </p>
          <p className="crew-tnum text-2xl font-semibold leading-tight" style={{ color: t.fg }}>
            {value}
          </p>
        </div>
      </div>
    </Card>
  );
};

const HelpButton = ({ text }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold"
        style={{
          background: "#fff",
          border: "1px solid var(--crew-line)",
          color: "var(--crew-ink-3)",
        }}
        aria-label="Help"
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        ?
      </button>
      {open && (
        <span
          className="absolute left-0 top-6 z-20 w-64 rounded-lg p-3 text-xs leading-5"
          style={{
            background: "#fff",
            border: "1px solid var(--crew-line)",
            color: "var(--crew-ink-2)",
            boxShadow: "var(--crew-shadow)",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="block">
    <span
      className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.08em]"
      style={{ color: "var(--crew-ink-3)" }}
    >
      {label}
      <HelpButton text={hint} />
    </span>
    {children}
  </div>
);

const inputClass =
  "block w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2";
const inputStyle = {
  background: "#fff",
  border: "1px solid var(--crew-line)",
  color: "var(--crew-ink)",
};

/* ──────────────────────────────────────────────────────────────────────────
 * Date / availability helpers — preserved from original
 * ────────────────────────────────────────────────────────────────────────── */
const weekdayOptions = [
  { value: 1, key: "crew.weekdays.monday" },
  { value: 2, key: "crew.weekdays.tuesday" },
  { value: 3, key: "crew.weekdays.wednesday" },
  { value: 4, key: "crew.weekdays.thursday" },
  { value: 5, key: "crew.weekdays.friday" },
  { value: 6, key: "crew.weekdays.saturday" },
  { value: 0, key: "crew.weekdays.sunday" },
];

const roleSuggestionKeys = [
  "crew.shift.roles.shift_lead",
  "crew.shift.roles.cashier",
  "crew.shift.roles.kitchen",
  "crew.shift.roles.service",
  "crew.shift.roles.runner",
  "crew.shift.roles.cleanup",
  "crew.shift.roles.closing",
];

const toDateInput = (value) => {
  const local = formatLocalDateTimeInput(value);
  return local ? local.substring(0, 10) : todayDateInput();
};

const timePart = (value, fallback) => {
  const local = formatLocalDateTimeInput(value);
  return local?.substring(11, 16) || fallback;
};

const combineDateAndTime = (date, time) => `${date}T${time || "08:00"}`;

const addDaysToDateInput = (date, days) => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return formatDateInputFromDate(d);
};

const isEndAfterStart = (startsAt, endsAt) => {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  return !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start;
};

const formatDateInputFromDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const monthCalendarDays = (monthValue) => {
  const base = new Date(`${monthValue || todayDateInput().substring(0, 7)}-01T00:00:00`);
  if (Number.isNaN(base.getTime())) return [];
  const start = new Date(base);
  const startOffset = start.getDay() === 0 ? 6 : start.getDay() - 1;
  start.setDate(start.getDate() - startOffset);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return {
      date: formatDateInputFromDate(d),
      day: d.getDate(),
      currentMonth: d.getMonth() === base.getMonth(),
      weekday: d.getDay(),
    };
  });
};

const monthRange = (monthValue) => {
  const [year, month] = (monthValue || todayDateInput().substring(0, 7)).split("-").map(Number);
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  return {
    from: `${formatDateInputFromDate(first)}T00:00`,
    to: `${formatDateInputFromDate(last)}T23:59`,
    days: last.getDate(),
  };
};

const periodRange = (fromDate, toDate) => {
  const first = fromDate || todayDateInput();
  const last = toDate || first;
  const from = first <= last ? first : last;
  const to = first <= last ? last : first;
  return {
    from: `${from}T00:00`,
    to: `${to}T23:59`,
  };
};

const dateRangeDays = (fromDate, toDate) => {
  const first = fromDate || todayDateInput();
  const last = toDate || first;
  const start = new Date(`${first <= last ? first : last}T00:00:00`);
  const end = new Date(`${first <= last ? last : first}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  const days = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    days.push({
      date: formatDateInputFromDate(d),
      day: d.getDate(),
      currentMonth: true,
      weekday: d.getDay(),
    });
  }
  return days;
};

const groupShiftsByDate = (items = []) => {
  const groups = new Map();
  items.forEach((shift) => {
    const start = new Date(shift.starts_at);
    const end = new Date(shift.ends_at);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = formatDateInputFromDate(d);
      const current = groups.get(day) || [];
      current.push(shift);
      groups.set(day, current);
    }
  });
  return groups;
};

const minutesForShiftOnDay = (shift, day) => {
  const dayStart = new Date(`${day}T00:00:00`);
  const dayEnd = new Date(`${day}T23:59:59`);
  const start = new Date(shift.starts_at);
  const end = new Date(shift.ends_at);
  if ([dayStart, dayEnd, start, end].some((d) => Number.isNaN(d.getTime()))) {
    return { start: 0, end: 0 };
  }
  const clampedStart = Math.max(start.getTime(), dayStart.getTime());
  const clampedEnd = Math.min(end.getTime(), dayEnd.getTime());
  return {
    start: Math.max(0, Math.round((clampedStart - dayStart.getTime()) / 60000)),
    end: Math.min(1440, Math.round((clampedEnd - dayStart.getTime()) / 60000)),
  };
};

/* Convert a shift to fractional hours within a day, clamped to dayStart..dayEnd. */
const shiftHoursOnDay = (shift, day, dayStart = 0, dayEnd = 24) => {
  const { start, end } = minutesForShiftOnDay(shift, day);
  return {
    start: Math.max(dayStart, start / 60),
    end: Math.min(dayEnd, end / 60),
  };
};

const assignmentOfferExpired = (assignment, shift) => {
  if (assignment?.status !== "offered") return false;
  if (!shift?.response_deadline_at) return false;
  const deadline = new Date(shift.response_deadline_at);
  return !Number.isNaN(deadline.getTime()) && deadline < new Date();
};

const assignmentStatusRank = { accepted: 5, assigned: 4, offered: 3, declined: 2, cancelled: 1 };

const dedupeAssignments = (items = []) => {
  const byShiftAndUser = new Map();
  items.forEach((assignment) => {
    const key = `${assignment.shift_id || "shift"}:${assignment.user_id || "open"}:${assignment.user_id ? "user" : assignment.id}`;
    const current = byShiftAndUser.get(key);
    if (!current || (assignmentStatusRank[assignment.status] || 0) > (assignmentStatusRank[current.status] || 0)) {
      byShiftAndUser.set(key, assignment);
    }
  });
  return Array.from(byShiftAndUser.values());
};

const dedupeAvailability = (items = []) => {
  const bySlot = new Map();
  items.forEach((availability) => {
    const key = `${availability.user_id}:${availability.starts_at}:${availability.ends_at}`;
    const current = bySlot.get(key);
    if (!current || new Date(availability.updated_at || 0) > new Date(current.updated_at || 0)) {
      bySlot.set(key, availability);
    }
  });
  return Array.from(bySlot.values()).sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
};

const availabilityHours = (availability) => {
  const start = new Date(availability.starts_at);
  const end = new Date(availability.ends_at);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return 0;
  return (end - start) / 3600000;
};

const groupAvailabilityByMonth = (items = []) => {
  const groups = new Map();
  items.forEach((availability) => {
    const month = String(availability.starts_at || "").substring(0, 7) || "unknown";
    const current = groups.get(month) || { month, items: [], days: new Set(), hours: 0 };
    current.items.push(availability);
    current.days.add(String(availability.starts_at || "").substring(0, 10));
    if (availability.available) current.hours += availabilityHours(availability);
    groups.set(month, current);
  });
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      dayCount: group.days.size,
      hours: Math.round(group.hours * 100) / 100,
    }))
    .sort((a, b) => b.month.localeCompare(a.month));
};

/* Get the Monday of the week containing `date` (date is yyyy-mm-dd). */
const mondayOf = (date) => {
  const d = new Date(`${date}T00:00:00`);
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return d;
};

/* Build a 7-day strip starting from mondayOf(date). */
const weekDaysFor = (date) => {
  const monday = mondayOf(date);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      date: formatDateInputFromDate(d),
      day: d.getDate(),
      weekday: d.getDay(),
      label: d.toLocaleDateString(undefined, { weekday: "short" }),
    };
  });
};

/* ──────────────────────────────────────────────────────────────────────────
 * ShiftDialog — re-skinned, uses RangeTimeSlider for start/end
 * ────────────────────────────────────────────────────────────────────────── */
function ShiftDialog({ open, onClose, onSaved, shift, copyFrom, project }) {
  const { t } = useTranslation();
  const sourceShift = shift || copyFrom;
  const isCopy = Boolean(copyFrom && !shift?.id);
  const [form, setForm] = useState(emptyShiftForm);
  const [activeStep, setActiveStep] = useState("info");
  const [calendar, setCalendar] = useState({
    selectedDates: [todayDateInput()],
    month: todayDateInput().substring(0, 7),
    start_time: "08:00",
    end_time: "16:00",
  });
  const [assignedUser, setAssignedUser] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const defaultForm = emptyShiftForm();
    const next = sourceShift
      ? {
          title: sourceShift.title || "",
          location: sourceShift.location || "",
          role_label: sourceShift.role_label || "",
          starts_at: formatLocalDateTimeInput(sourceShift.starts_at),
          ends_at: formatLocalDateTimeInput(sourceShift.ends_at),
          break_minutes: sourceShift.break_minutes ?? 30,
          slots: sourceShift.slots ?? 1,
          assignment_mode: sourceShift.assignment_mode || "direct",
          status: shift?.id ? sourceShift.status || "draft" : "draft",
          leader_checklist_required: Boolean(sourceShift.leader_checklist_required),
          handover_note: sourceShift.handover_note || "",
          project_id: sourceShift.project_id || project?.id || "",
          project_activity_id: sourceShift.project_activity_id || "",
          work_order_id: sourceShift.work_order_id || "",
          response_deadline_at: shift?.id ? formatLocalDateTimeInput(sourceShift.response_deadline_at) : "",
        }
      : { ...defaultForm, project_id: project?.id || "" };
    setForm(next);
    setCalendar({
      selectedDates: [toDateInput(next.starts_at)],
      month: toDateInput(next.starts_at).substring(0, 7),
      start_time: timePart(next.starts_at, "08:00"),
      end_time: timePart(next.ends_at, "16:00"),
    });
    setActiveStep("info");
    setAssignedUser(null);
  }, [sourceShift, shift?.id, project?.id]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const selectedProject = useMemo(() => {
    if (project?.id && String(project.id) === String(form.project_id)) return project;
    return form.project_id ? { id: form.project_id } : null;
  }, [form.project_id, project]);

  const setShiftProject = (nextProject) => {
    set("project_id", nextProject?.id || "");
  };

  const toggleCalendarDate = (day) => {
    setCalendar((prev) => {
      const selected = prev.selectedDates || [];
      return {
        ...prev,
        selectedDates: selected.includes(day) ? selected.filter((d) => d !== day) : [...selected, day].sort(),
      };
    });
  };

  const removeCalendarDate = (dateToRemove) => {
    setCalendar((prev) => ({
      ...prev,
      selectedDates: (prev.selectedDates || []).filter((d) => d !== dateToRemove),
    }));
  };

  const buildShiftTimes = (day) => {
    const startsAt = combineDateAndTime(day, calendar.start_time);
    const endDate = calendar.end_time <= calendar.start_time ? addDaysToDateInput(day, 1) : day;
    const endsAt = combineDateAndTime(endDate, calendar.end_time);
    return { startsAt, endsAt };
  };

  const assignmentsToCopy = useMemo(() => {
    if (assignedUser?.id) return [];
    const sourceAssignments = (copyFrom || shift)?.assignments || [];
    return dedupeAssignments(sourceAssignments).filter(
      (assignment) => assignment.user_id && !["declined", "cancelled"].includes(assignment.status)
    );
  }, [assignedUser?.id, copyFrom, shift]);

  /* RangeTimeSlider works in fractional-hours; sync to HH:MM strings. */
  const sliderStart = useMemo(() => {
    const [h, m] = (calendar.start_time || "08:00").split(":").map(Number);
    return h + (m || 0) / 60;
  }, [calendar.start_time]);
  const sliderEnd = useMemo(() => {
    const [h, m] = (calendar.end_time || "16:00").split(":").map(Number);
    const v = h + (m || 0) / 60;
    return v <= sliderStart ? Math.min(24, sliderStart + 1) : v;
  }, [calendar.end_time, sliderStart]);

  const onSliderChange = (s, e) => {
    setCalendar((prev) => ({
      ...prev,
      start_time: fmtHM(s),
      end_time: fmtHM(e),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const selectedDates = calendar.selectedDates || [];
      const payload = {
        ...form,
        project_id: form.project_id || undefined,
        project_activity_id: form.project_activity_id || undefined,
        work_order_id: form.work_order_id || undefined,
        response_deadline_at: form.response_deadline_at || undefined,
        break_minutes: Number(form.break_minutes || 0),
        slots: Number(form.slots || 1),
        leader_checklist_required: Boolean(form.leader_checklist_required),
        handover_note: form.handover_note || undefined,
      };
      const createShiftForDate = (d) => {
        const { startsAt, endsAt } = buildShiftTimes(d);
        if (!isEndAfterStart(startsAt, endsAt)) {
          throw new Error(t("crew.shift.ends_after_start"));
        }
        return crewApi.createShift({
          ...payload,
          starts_at: startsAt,
          ends_at: endsAt,
        });
      };
      const copyAssignmentsToCreatedShifts = async (created) => {
        const createdShifts = created.map((res) => res.data?.data).filter(Boolean);
        if (assignedUser?.id) {
          await Promise.all(
            createdShifts.map((createdShift) =>
              crewApi.createAssignment({
                shift_id: createdShift.id,
                user_id: assignedUser.id,
                status: "offered",
              })
            )
          );
        } else if (assignmentsToCopy.length > 0) {
          await Promise.all(
            createdShifts.flatMap((createdShift) =>
              assignmentsToCopy.map((assignment) =>
                crewApi.createAssignment({
                  shift_id: createdShift.id,
                  user_id: assignment.user_id,
                  status: ["accepted", "assigned"].includes(assignment.status) ? "assigned" : "offered",
                })
              )
            )
          );
        }
      };
      if (shift?.id) {
        const datesToSave = selectedDates.length > 0 ? selectedDates : [toDateInput(payload.starts_at)];
        const [date, ...extraDates] = datesToSave;
        const { startsAt, endsAt } = buildShiftTimes(date);
        const updatePayload = { ...payload, starts_at: startsAt, ends_at: endsAt };
        if (!isEndAfterStart(updatePayload.starts_at, updatePayload.ends_at)) {
          toast.error(t("crew.shift.ends_after_start"));
          return;
        }
        await crewApi.updateShift(shift.id, updatePayload);
        if (extraDates.length > 0) {
          const created = await Promise.all(extraDates.map(createShiftForDate));
          await copyAssignmentsToCreatedShifts(created);
        }
      } else {
        if (selectedDates.length === 0) {
          toast.error(t("crew.shift.no_days_selected"));
          return;
        }
        const created = await Promise.all(selectedDates.map(createShiftForDate));
        await copyAssignmentsToCreatedShifts(created);
      }
      toast.success(t("crew.toast.saved"));
      await onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog open={open} onClose={onClose} title={shift?.id ? "crew.shift.edit" : isCopy ? "crew.shift.copy" : "crew.shift.new"} size="3xl">
      <div className="crew-scope">
        <style>{SCOPE_STYLE}</style>
        <div className="space-y-5">
          <Segmented
            options={[
              { value: "info", label: t("crew.shift.step_info") },
              { value: "days", label: t("crew.shift.step_days_time") },
            ]}
            value={activeStep}
            onChange={setActiveStep}
          />

          {activeStep === "info" && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("crew.shift.title")} hint={t("crew.shift.title_hint")}>
                <input className={inputClass} style={inputStyle} placeholder={t("crew.shift.title_placeholder")} value={form.title} onChange={(e) => set("title", e.target.value)} />
              </Field>
              <Field label={t("crew.shift.role")} hint={t("crew.shift.role_hint")}>
                <div className="space-y-2">
                  <select
                    className={inputClass}
                    style={inputStyle}
                    value=""
                    onChange={(e) => {
                      if (e.target.value) set("role_label", e.target.value);
                    }}
                  >
                    <option value="">{t("crew.shift.role_suggestions")}</option>
                    {roleSuggestionKeys.map((key) => (
                      <option key={key} value={t(key)}>
                        {t(key)}
                      </option>
                    ))}
                  </select>
                  <input className={inputClass} style={inputStyle} placeholder={t("crew.shift.role_placeholder")} value={form.role_label} onChange={(e) => set("role_label", e.target.value)} />
                </div>
              </Field>
              <Field label={t("crew.shift.project")} hint={t("crew.shift.project_hint")}>
                <ProjectSelector label={false} selected={selectedProject} onChange={(e) => setShiftProject(e.target.object)} />
              </Field>
              <Field label={t("crew.shift.location")} hint={t("crew.shift.location_hint")}>
                <input className={inputClass} style={inputStyle} placeholder={t("crew.shift.location_placeholder")} value={form.location} onChange={(e) => set("location", e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("crew.shift.break_minutes")} hint={t("crew.shift.break_minutes_hint")}>
                  <input type="number" className={inputClass} style={inputStyle} value={form.break_minutes} onChange={(e) => set("break_minutes", e.target.value)} />
                </Field>
                <Field label={t("crew.shift.slots")} hint={t("crew.shift.slots_hint")}>
                  <input type="number" min="1" className={inputClass} style={inputStyle} value={form.slots} onChange={(e) => set("slots", e.target.value)} />
                </Field>
              </div>
              <Field label={t("crew.shift.assignment_mode")} hint={t("crew.shift.assignment_mode_hint")}>
                <select className={inputClass} style={inputStyle} value={form.assignment_mode} onChange={(e) => set("assignment_mode", e.target.value)}>
                  <option value="direct">{t("crew.shift.assignment_direct")}</option>
                  <option value="open_offer">{t("crew.shift.assignment_open")}</option>
                </select>
              </Field>
              <Field label={t("crew.shift.response_deadline")} hint={t("crew.shift.response_deadline_hint")}>
                <input type="datetime-local" className={inputClass} style={inputStyle} value={form.response_deadline_at} onChange={(e) => set("response_deadline_at", e.target.value)} />
              </Field>
              <div className="md:col-span-2 rounded-2xl p-4" style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line)" }}>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                    checked={Boolean(form.leader_checklist_required)}
                    onChange={(e) => set("leader_checklist_required", e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.shift.leader_checklist_required")}</span>
                    <span className="mt-1 block text-xs" style={{ color: "var(--crew-ink-3)" }}>{t("crew.shift.leader_checklist_required_hint")}</span>
                  </span>
                </label>
                <div className="mt-3">
                  <Field label={t("crew.shift.handover_note")} hint={t("crew.shift.handover_note_hint")}>
                    <textarea
                      className={inputClass}
                      style={{ ...inputStyle, minHeight: 78 }}
                      value={form.handover_note}
                      onChange={(e) => set("handover_note", e.target.value)}
                    />
                  </Field>
                </div>
              </div>
              {!shift?.id && (
                <div className="md:col-span-2">
                  <Field label={t("crew.shift.assigned_employee")} hint={t("crew.shift.assigned_employee_hint")}>
                    <EmployeeSelector includeHired selected={assignedUser} onChange={(e) => setAssignedUser(e.target.object)} />
                  </Field>
                  {assignmentsToCopy.length > 0 && (
                    <p className="mt-2 text-xs" style={{ color: "var(--crew-ink-3)" }}>
                      {t("crew.shift.copy_assignments_hint", { count: assignmentsToCopy.length })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeStep === "days" && (
            <div className="rounded-2xl p-5" style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line)" }}>
              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label={t("crew.shift.month")} hint={t("crew.shift.month_hint")}>
                  <input
                    type="month"
                    className={inputClass}
                    style={inputStyle}
                    value={calendar.month}
                    onChange={(e) => setCalendar((prev) => ({ ...prev, month: e.target.value }))}
                  />
                </Field>
                <div className="md:col-span-2">
                  <RangeTimeSlider start={sliderStart} end={sliderEnd} onChange={onSliderChange} />
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium" style={{ color: "var(--crew-ink-3)" }}>
                {weekdayOptions.map((d) => (
                  <div key={d.value} className="py-1">{t(d.key)}</div>
                ))}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {monthCalendarDays(calendar.month).map((day) => {
                  const selected = (calendar.selectedDates || []).includes(day.date);
                  return (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => toggleCalendarDate(day.date)}
                      className="min-h-[62px] rounded-xl p-2 text-left transition sm:min-h-[82px]"
                      style={{
                        border: `1px solid ${selected ? "var(--crew-terracotta)" : "var(--crew-line-2)"}`,
                        background: selected ? "var(--crew-terracotta-soft)" : "var(--crew-paper)",
                        color: selected ? "var(--crew-terracotta-ink)" : "var(--crew-ink-2)",
                        opacity: day.currentMonth ? 1 : 0.4,
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <span className="crew-tnum text-sm font-semibold">{day.day}</span>
                        {selected && (
                          <span
                            className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white"
                            style={{ background: "var(--crew-terracotta)" }}
                          >
                            ✓
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(calendar.selectedDates || []).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => removeCalendarDate(d)}
                    className="rounded-full px-3 py-1 text-xs font-medium transition"
                    style={{
                      background: "#fff",
                      border: "1px solid var(--crew-line)",
                      color: "var(--crew-ink-2)",
                    }}
                  >
                    {d} ×
                  </button>
                ))}
                {(calendar.selectedDates || []).length === 0 && (
                  <span className="text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.shift.no_days_selected")}</span>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between gap-2 pt-4" style={{ borderTop: "1px solid var(--crew-line)" }}>
            <Button onClick={onClose}>{t("Cancel")}</Button>
            <div className="flex gap-2">
              {activeStep === "info" && (
                <Button onClick={() => setActiveStep("days")}>{t("crew.shift.next_days_time")}</Button>
              )}
              {activeStep === "days" && (
                <Button onClick={() => setActiveStep("info")}>{t("crew.shift.back_info")}</Button>
              )}
              <Button variant="primary" onClick={save} disabled={saving || !form.title || (calendar.selectedDates || []).length === 0}>
                {saving ? t("Saving") : shift?.id ? t("Save") : isCopy ? t("crew.shift.copy_plan") : t("crew.shift.create_plan")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PMDialog>
  );
}

function AssignmentDialog({ open, onClose, onSaved, shift }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!shift?.id || !user?.id) return;
    setSaving(true);
    try {
      await crewApi.createAssignment({ shift_id: shift.id, user_id: user.id, status: "offered" });
      toast.success(t("crew.toast.assignment_saved"));
      setUser(null);
      await onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog open={open} onClose={onClose} title="crew.assignment.add" size="xl">
      <div className="crew-scope">
        <style>{SCOPE_STYLE}</style>
        <div className="space-y-4">
          <EmployeeSelector includeHired selected={user} onChange={(e) => setUser(e.target.object)} />
          <div className="flex justify-end gap-2 pt-4" style={{ borderTop: "1px solid var(--crew-line)" }}>
            <Button onClick={onClose}>{t("Cancel")}</Button>
            <Button variant="primary" onClick={save} disabled={!user?.id || saving}>
              {t("Save")}
            </Button>
          </div>
        </div>
      </div>
    </PMDialog>
  );
}

function KioskDialog({ open, onClose, onVerified }) {
  const { t } = useTranslation();
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    setLoading(true);
    try {
      const res = await crewApi.verifyKioskPin({ employee_number: employeeNumber, pin });
      toast.success(t("crew.toast.pin_verified"));
      onVerified?.(res.data?.data);
      setEmployeeNumber("");
      setPin("");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PMDialog open={open} onClose={onClose} title="crew.kiosk.title" size="md">
      <div className="crew-scope">
        <style>{SCOPE_STYLE}</style>
        <div className="space-y-4">
          <Field label={t("crew.kiosk.employee_number")}>
            <input className={inputClass} style={inputStyle} value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} />
          </Field>
          <Field label={t("crew.kiosk.pin")}>
            <input type="password" inputMode="numeric" className={inputClass} style={inputStyle} value={pin} onChange={(e) => setPin(e.target.value)} />
          </Field>
          <Button variant="primary" className="w-full" onClick={verify} disabled={loading || !employeeNumber || !pin}>
            {t("crew.kiosk.verify")}
          </Button>
        </div>
      </div>
    </PMDialog>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Week timeline — the headline view.
 * Rows = assignees (and an "Open shifts" row), columns = hours within the day.
 * dayIdx selects which day of the week to detail.
 * ────────────────────────────────────────────────────────────────────────── */
function WeekTimeline({
  weekDays,
  dayIdx,
  setDayIdx,
  date,
  setDate,
  onSelectShift,
  selectedShiftId,
  shiftsByDate,
  density = "cozy",
}) {
  const { t } = useTranslation();
  const dayStart = 6;
  const dayEnd = 24;
  const pxPerHour = 38;
  const rowHeight = density === "compact" ? 52 : 64;
  const barHeight = density === "compact" ? 36 : 44;

  const selectedDate = weekDays[dayIdx]?.date || date;
  const todayShifts = (shiftsByDate.get(selectedDate) || []).slice();

  /* Group shifts on the selected day by assignee. Open shifts (no assignment) live in their own row. */
  const grouped = useMemo(() => {
    const byUser = new Map();
    const open = [];
    todayShifts.forEach((shift) => {
      const assigned = dedupeAssignments(shift.assignments || []).filter((a) => a.user_id && !["declined", "cancelled"].includes(a.status));
      if (assigned.length === 0) {
        open.push({ shift, assignment: null });
        return;
      }
      assigned.forEach((a) => {
        const key = String(a.user_id);
        const list = byUser.get(key) || {
          key,
          name: a.user?.displayTitle || a.user?.email || `#${a.user_id}`,
          role: shift.role_label || "",
          items: [],
        };
        list.items.push({ shift, assignment: a });
        byUser.set(key, list);
      });
    });
    return { byUser: Array.from(byUser.values()), open };
  }, [todayShifts]);

  /* Hour-by-hour coverage for the top mini chart. */
  const coverage = useMemo(() => {
    const arr = new Array(dayEnd - dayStart).fill(0);
    todayShifts.forEach((shift) => {
      const { start, end } = shiftHoursOnDay(shift, selectedDate, dayStart, dayEnd);
      const slots = Math.max(1, Number(shift.slots || 1));
      for (let h = Math.floor(start); h < Math.ceil(end); h += 1) {
        const idx = h - dayStart;
        if (idx >= 0 && idx < arr.length) arr[idx] += slots;
      }
    });
    return arr;
  }, [todayShifts, selectedDate]);

  const maxCoverage = Math.max(1, ...coverage);
  const totalShifts = todayShifts.length;
  const totalHours = todayShifts.reduce((sum, s) => {
    const { start, end } = shiftHoursOnDay(s, selectedDate, dayStart, dayEnd);
    return sum + Math.max(0, end - start);
  }, 0);

  const peopleColumnWidth = 220;
  const timelineWidth = (dayEnd - dayStart) * pxPerHour;

  return (
    <Card className="overflow-hidden">
      {/* Toolbar — week strip + KPI tiles + add button */}
      <div
        className="flex flex-wrap items-center gap-4 px-5 py-4"
        style={{ background: "var(--crew-paper-2)", borderBottom: "1px solid var(--crew-line)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-lg"
            style={{ background: "linear-gradient(135deg, var(--crew-terracotta), var(--crew-apricot))", color: "#fff" }}
          >
            <CalendarDaysIcon className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
              {t("crew.shift.week_of", { defaultValue: "Week of" })} {weekDays[0]?.label} {weekDays[0]?.day}
            </div>
            <div style={{ fontSize: 11, color: "var(--crew-ink-3)" }}>
              {selectedDate}
            </div>
          </div>
        </div>

        <div
          className="ml-2 flex gap-1 rounded-xl p-1"
          style={{ background: "var(--crew-sand)" }}
        >
          {weekDays.map((d, i) => {
            const active = i === dayIdx;
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => {
                  setDayIdx(i);
                  setDate(d.date);
                }}
                className="flex flex-col items-center justify-center"
                style={{
                  width: 56,
                  padding: "6px 0",
                  border: "none",
                  background: active ? "#fff" : "transparent",
                  borderRadius: 9,
                  cursor: "pointer",
                  boxShadow: active ? "0 1px 3px rgba(40,30,20,.08)" : "none",
                  color: active ? "var(--crew-ink)" : "var(--crew-ink-3)",
                  transition: "background .2s",
                  fontFamily: "inherit",
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".06em" }}>
                  {d.label}
                </span>
                <span className="crew-tnum" style={{ fontSize: 16, fontWeight: 600 }}>{d.day}</span>
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-5">
          <div className="flex items-center gap-5 pr-5" style={{ borderRight: "1px solid var(--crew-line)" }}>
            <div className="flex flex-col leading-tight">
              <span style={{ fontSize: 10, color: "var(--crew-ink-3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
                {t("crew.stats.shifts")}
              </span>
              <span className="crew-tnum" style={{ fontSize: 18, fontWeight: 600 }}>{totalShifts}</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span style={{ fontSize: 10, color: "var(--crew-ink-3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
                Hours
              </span>
              <span className="crew-tnum" style={{ fontSize: 18, fontWeight: 600 }}>
                {totalHours.toFixed(1)}
                <span style={{ fontSize: 11, fontWeight: 500, color: "var(--crew-ink-3)", marginLeft: 1 }}>h</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline body */}
      <div className="crew-track" style={{ overflowX: "auto", background: "var(--crew-paper)" }}>
        <div style={{ display: "flex", minWidth: peopleColumnWidth + timelineWidth }}>
          {/* People column (sticky) */}
          <div
            style={{
              position: "sticky",
              left: 0,
              zIndex: 5,
              width: peopleColumnWidth,
              background: "var(--crew-paper)",
              borderRight: "1px solid var(--crew-line)",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                height: 56,
                display: "flex",
                alignItems: "center",
                padding: "0 18px",
                fontSize: 11,
                color: "var(--crew-ink-3)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
                fontWeight: 500,
                background: "var(--crew-paper)",
                borderBottom: "1px solid var(--crew-line)",
              }}
            >
              <span>{t("crew.tabs.shifts")}</span>
              <span style={{ marginLeft: "auto", color: "var(--crew-ink-4)", textTransform: "none", letterSpacing: 0 }}>
                {grouped.byUser.length + (grouped.open.length > 0 ? 1 : 0)}
              </span>
            </div>
            {grouped.byUser.map((p) => {
              const personHours = p.items.reduce((acc, it) => {
                const { start, end } = shiftHoursOnDay(it.shift, selectedDate, dayStart, dayEnd);
                return acc + Math.max(0, end - start);
              }, 0);
              return (
                <div
                  key={p.key}
                  style={{
                    height: rowHeight,
                    padding: "0 18px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    borderBottom: "1px solid var(--crew-line-2)",
                  }}
                >
                  <Avatar label={p.name} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        letterSpacing: "-0.01em",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--crew-ink-3)", display: "flex", gap: 6, alignItems: "center" }}>
                      {p.role && <span>{p.role}</span>}
                      {p.role && <span style={{ color: "var(--crew-ink-4)" }}>·</span>}
                      <span className="crew-mono">{personHours.toFixed(1).replace(".0", "")}h</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {grouped.open.length > 0 && (
              <div
                style={{
                  height: rowHeight,
                  padding: "0 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  borderBottom: "1px solid var(--crew-line-2)",
                  background: "var(--crew-paper-2)",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "2px dashed var(--crew-line)",
                    color: "var(--crew-ink-3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  ?
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--crew-terracotta-ink)" }}>
                    {t("crew.shift.no_crew_added")}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--crew-ink-3)" }}>
                    {grouped.open.length} {t("crew.tabs.shifts").toLowerCase()}
                  </div>
                </div>
              </div>
            )}
            {grouped.byUser.length + grouped.open.length === 0 && (
              <div style={{ padding: "20px 18px", color: "var(--crew-ink-3)", fontSize: 12 }}>
                {t("crew.shift.no_shifts_selected_day")}
              </div>
            )}
          </div>

          {/* Timeline + ruler + rows */}
          <div style={{ flex: 1, position: "relative", minWidth: timelineWidth }}>
            {/* Coverage ruler header */}
            <div
              style={{
                height: 56,
                position: "relative",
                borderBottom: "1px solid var(--crew-line)",
                background: "var(--crew-paper)",
              }}
            >
              <div style={{ position: "absolute", top: 6, left: 0, right: 0, height: 16 }}>
                {Array.from({ length: dayEnd - dayStart + 1 }, (_, i) => i + dayStart).map((h) => (
                  <div
                    key={h}
                    style={{
                      position: "absolute",
                      left: (h - dayStart) * pxPerHour - 12,
                      width: 24,
                      textAlign: "center",
                    }}
                  >
                    <span className="crew-mono" style={{ fontSize: 10, color: "var(--crew-ink-4)" }}>
                      {String(h).padStart(2, "0")}
                    </span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 28,
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                {coverage.map((v, i) => (
                  <div
                    key={i}
                    style={{
                      width: pxPerHour,
                      padding: "0 2px",
                      height: "100%",
                      display: "flex",
                      alignItems: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: `${(v / maxCoverage) * 100}%`,
                        minHeight: 2,
                        background: v === 0 ? "var(--crew-line)" : v < 2 ? "#F4CDB8" : "var(--crew-sage)",
                        borderRadius: "3px 3px 0 0",
                        opacity: v === 0 ? 0.5 : 1,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Person rows with bars */}
            {grouped.byUser.map((p, i) => (
              <div
                key={p.key}
                style={{
                  position: "relative",
                  height: rowHeight,
                  borderBottom: "1px solid var(--crew-line-2)",
                  background: i % 2 ? "var(--crew-paper)" : "var(--crew-paper-2)",
                }}
              >
                {/* Hour grid lines */}
                {Array.from({ length: dayEnd - dayStart + 1 }, (_, h) => (
                  <div
                    key={h}
                    style={{
                      position: "absolute",
                      left: h * pxPerHour,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: h % 6 === 0 ? "var(--crew-line)" : "var(--crew-line-2)",
                      opacity: h === 0 ? 0 : 1,
                    }}
                  />
                ))}
                {p.items.map(({ shift }) => {
                  const { start, end } = shiftHoursOnDay(shift, selectedDate, dayStart, dayEnd);
                  if (end <= start) return null;
                  const palette = personColors(p.name);
                  return (
                    <StretchyTimeBar
                      key={`${p.key}-${shift.id}`}
                      start={start}
                      end={end}
                      dayStart={dayStart}
                      dayEnd={dayEnd}
                      pxPerHour={pxPerHour}
                      color={palette.color}
                      soft={palette.soft}
                      deep={palette.deep}
                      label={shift.title || shift.role_label || ""}
                      sub={`${fmtHM(start)}–${fmtHM(end)}`}
                      selected={shift.id === selectedShiftId}
                      onSelect={() => onSelectShift(shift)}
                      height={barHeight}
                      readOnly
                    />
                  );
                })}
              </div>
            ))}
            {grouped.open.length > 0 && (
              <div
                style={{
                  position: "relative",
                  height: rowHeight,
                  borderBottom: "1px solid var(--crew-line-2)",
                  background: "var(--crew-paper-2)",
                }}
              >
                {Array.from({ length: dayEnd - dayStart + 1 }, (_, h) => (
                  <div
                    key={h}
                    style={{
                      position: "absolute",
                      left: h * pxPerHour,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: h % 6 === 0 ? "var(--crew-line)" : "var(--crew-line-2)",
                      opacity: h === 0 ? 0 : 1,
                    }}
                  />
                ))}
                {grouped.open.map(({ shift }) => {
                  const { start, end } = shiftHoursOnDay(shift, selectedDate, dayStart, dayEnd);
                  if (end <= start) return null;
                  return (
                    <StretchyTimeBar
                      key={`open-${shift.id}`}
                      start={start}
                      end={end}
                      dayStart={dayStart}
                      dayEnd={dayEnd}
                      pxPerHour={pxPerHour}
                      color="#B8A88F"
                      soft="#F4ECDD"
                      deep="#7A6A57"
                      label={`${t("crew.shift.no_crew_added")}`}
                      sub={shift.title || ""}
                      selected={shift.id === selectedShiftId}
                      onSelect={() => onSelectShift(shift)}
                      height={barHeight}
                      readOnly
                    />
                  );
                })}
              </div>
            )}

            {/* Empty CTA */}
            <div
              style={{
                height: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--crew-ink-4)",
                fontSize: 12,
                gap: 6,
                borderTop: "1px dashed var(--crew-line)",
              }}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {t("crew.shift.add_shift_hint")}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Detail panel (alongside week timeline) — shows the selected shift.
 * ────────────────────────────────────────────────────────────────────────── */
function ShiftDetailPanel({ shift, onEdit, onCopy, onAssign, onPublish, onCancel }) {
  const { t } = useTranslation();
  if (!shift) {
    return (
      <Card className="p-6">
        <div
          className="rounded-xl p-6 text-center"
          style={{ background: "var(--crew-paper-2)", border: "1px dashed var(--crew-line)" }}
        >
          <p className="text-sm" style={{ color: "var(--crew-ink-3)" }}>
            {t("crew.shift.no_shifts_selected_day")}
          </p>
        </div>
      </Card>
    );
  }
  const assigned = dedupeAssignments(shift.assignments || []);
  const palette = personColors(shift.title || shift.id);
  const startStr = formatLocalDateTimeInput(shift.starts_at);
  const endStr = formatLocalDateTimeInput(shift.ends_at);
  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <span
          className="text-[11px] font-medium uppercase tracking-[.08em]"
          style={{ color: "var(--crew-ink-3)" }}
        >
          {t("crew.shift.edit", { defaultValue: "Shift detail" })}
        </span>
        <Pill tone="terracotta">{shift.status}</Pill>
      </div>
      <div className="mb-4 flex items-center gap-3">
        <Avatar label={shift.title || shift.role_label || "?"} size={44} ring />
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {shift.title || t("crew.shift.no_role")}
          </div>
          <div style={{ fontSize: 12, color: "var(--crew-ink-3)" }}>
            {shift.role_label || ""} {shift.location ? `· ${shift.location}` : ""}
          </div>
        </div>
      </div>
      <div
        className="mb-3 rounded-xl p-3"
        style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line)" }}
      >
        <div className="crew-mono" style={{ fontSize: 13, color: "var(--crew-ink)" }}>
          {startStr.replace("T", " ")} → {endStr.replace("T", " ")}
        </div>
        <div style={{ fontSize: 11, color: "var(--crew-ink-3)", marginTop: 2 }}>
          {t("crew.shift.break_minutes")}: {shift.break_minutes ?? 0} · {t("crew.shift.slots")}: {shift.slots ?? 1}
        </div>
      </div>
      <div className="mb-4">
        <div
          className="mb-2 text-[11px] font-medium uppercase tracking-[.08em]"
          style={{ color: "var(--crew-ink-3)" }}
        >
          {t("crew.assignment.add", { defaultValue: "Crew" })}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {assigned.length === 0 && (
            <Pill tone="warn">{t("crew.shift.no_crew_added")}</Pill>
          )}
          {assigned.map((a) => (
            <Pill key={a.id} tone={a.status === "accepted" || a.status === "assigned" ? "sage" : "neutral"}>
              {(a.user && (a.user.displayTitle || a.user.email)) || `#${a.user_id}`}
            </Pill>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onEdit}>{t("Edit")}</Button>
        <Button onClick={onCopy}>{t("crew.shift.copy_short")}</Button>
        <Button onClick={onAssign}>{t("crew.assignment.add_short")}</Button>
        {shift.status !== "published" && (
          <Button variant="accent" onClick={onPublish}>
            {t("crew.shift.publish")}
          </Button>
        )}
        {shift.status !== "cancelled" && (
          <Button variant="danger" onClick={onCancel}>
            {t("crew.shift.cancel")}
          </Button>
        )}
      </div>
      {/* hint about palette pulled from name hash for consistency with bar color */}
      <div className="mt-3" style={{ height: 4, borderRadius: 2, background: palette.color, opacity: 0.4 }} />
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Main page
 * ────────────────────────────────────────────────────────────────────────── */
export function CrewSchedulingContent() {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("shifts");
  const [project, setProject] = useState(null);
  const [date, setDate] = useState(searchParams.get("date") || todayDateInput());
  const [periodType, setPeriodType] = useState("week");
  const [period, setPeriod] = useState(() => {
    const initialDate = searchParams.get("date") || todayDateInput();
    const week = weekDaysFor(initialDate);
    return {
      from: week[0].date,
      to: week[week.length - 1].date,
    };
  });
  const [projectIdParam] = useState(searchParams.get("project_id") || "");
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [openPunches, setOpenPunches] = useState([]);
  const [punchHistory, setPunchHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [checklistRuns, setChecklistRuns] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [shiftDialog, setShiftDialog] = useState({ open: false, shift: null });
  const [assignmentDialog, setAssignmentDialog] = useState({ open: false, shift: null });
  const [kioskOpen, setKioskOpen] = useState(false);
  const [availabilitySaving, setAvailabilitySaving] = useState(false);
  const [expandedAvailabilityMonths, setExpandedAvailabilityMonths] = useState([]);
  const [pinUser, setPinUser] = useState(null);
  const [pinValue, setPinValue] = useState("");
  const [density, setDensity] = useState("cozy");
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [punchDeviationNote, setPunchDeviationNote] = useState("");
  const [availabilityForm, setAvailabilityForm] = useState({
    selectedDates: [date],
    month: date.substring(0, 7),
    start_time: "08:00",
    end_time: "16:00",
    available: true,
  });

  const selectedProjectId = project?.id || projectIdParam || undefined;
  const selectedMonth = date.substring(0, 7);
  const normalizedPeriod = useMemo(() => {
    const range = periodRange(period.from, period.to);
    return {
      from: range.from.substring(0, 10),
      to: range.to.substring(0, 10),
    };
  }, [period.from, period.to]);
  const weekDays = useMemo(() => weekDaysFor(date), [date]);
  const visibleScheduleRange = useMemo(() => {
    const weekFrom = weekDays[0]?.date || normalizedPeriod.from;
    const weekTo = weekDays[weekDays.length - 1]?.date || normalizedPeriod.to;
    return {
      from: weekFrom < normalizedPeriod.from ? weekFrom : normalizedPeriod.from,
      to: weekTo > normalizedPeriod.to ? weekTo : normalizedPeriod.to,
    };
  }, [normalizedPeriod.from, normalizedPeriod.to, weekDays]);
  const selectedPeriodRange = useMemo(
    () => periodRange(visibleScheduleRange.from, visibleScheduleRange.to),
    [visibleScheduleRange.from, visibleScheduleRange.to]
  );
  const availabilityMonth = activeTab === "availability"
    ? normalizedPeriod.from.substring(0, 7)
    : availabilityForm.month || selectedMonth;

  const dayIdx = useMemo(() => Math.max(0, weekDays.findIndex((d) => d.date === date)), [date, weekDays]);

  useEffect(() => {
    if (date < visibleScheduleRange.from) setDate(visibleScheduleRange.from);
    if (date > visibleScheduleRange.to) setDate(visibleScheduleRange.to);
  }, [date, visibleScheduleRange.from, visibleScheduleRange.to]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const shiftParams = {
        from: selectedPeriodRange.from,
        to: selectedPeriodRange.to,
        project_id: selectedProjectId || undefined,
      };
      const availabilityRange = monthRange(availabilityMonth);
      const checklistParams = {
        from: selectedPeriodRange.from,
        to: selectedPeriodRange.to,
        project_id: selectedProjectId || undefined,
      };
      const [shiftRes, assignmentRes, availabilityRes, openRes, checklistRes] = await Promise.all([
        crewApi.shifts(shiftParams),
        crewApi.assignments({}),
        crewApi.availabilities({ from: availabilityRange.from, to: availabilityRange.to }),
        crewApi.openPunches(),
        crewApi.shiftChecklists(checklistParams),
      ]);
      setShifts(shiftRes.data?.data || []);
      setAssignments(assignmentRes.data?.data || []);
      setAvailability(availabilityRes.data?.data || []);
      setOpenPunches(openRes.data?.data || []);
      setChecklistRuns(checklistRes.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [availabilityMonth, selectedPeriodRange, selectedProjectId]);

  const loadPunchHistory = useCallback(async () => {
    try {
      const res = await crewApi.timePunches({
        from: `${date.substring(0, 8)}01T00:00`,
        to: `${date}T23:59`,
      });
      setPunchHistory(res.data?.data || []);
    } catch {
      setPunchHistory([]);
    }
  }, [date]);

  const loadLeaderData = useCallback(async () => {
    try {
      const from = `${date.substring(0, 8)}01T00:00`;
      const to = `${date}T23:59`;
      const [alertRes, payrollRes] = await Promise.all([
        crewApi.complianceAlerts({}),
        crewApi.payrollPreview({ from, to }),
      ]);
      setAlerts(alertRes.data?.data || []);
      setPayroll(payrollRes.data?.data || null);
    } catch {
      setAlerts([]);
      setPayroll(null);
    }
  }, [date]);

  useEffect(() => {
    loadAll();
    loadLeaderData();
    loadPunchHistory();
  }, [loadAll, loadLeaderData, loadPunchHistory]);

  const shiftById = useMemo(() => Object.fromEntries(shifts.map((s) => [s.id, s])), [shifts]);
  const activeShifts = useMemo(() => shifts.filter((shift) => shift.status !== "cancelled"), [shifts]);
  const uniqueAssignments = useMemo(() => dedupeAssignments(assignments), [assignments]);
  const availabilityItems = useMemo(() => dedupeAvailability(availability), [availability]);
  const availabilityGroups = useMemo(() => groupAvailabilityByMonth(availabilityItems), [availabilityItems]);
  const shiftsByDate = useMemo(() => groupShiftsByDate(activeShifts), [activeShifts]);
  const scheduleDays = useMemo(
    () => dateRangeDays(visibleScheduleRange.from, visibleScheduleRange.to),
    [visibleScheduleRange.from, visibleScheduleRange.to]
  );
  const plannedDays = useMemo(
    () => scheduleDays.filter((day) => (shiftsByDate.get(day.date) || []).length > 0),
    [scheduleDays, shiftsByDate]
  );
  const selectedDayShifts = useMemo(
    () => [...(shiftsByDate.get(date) || [])].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)),
    [date, shiftsByDate]
  );
  const missingDays = scheduleDays.length - plannedDays.length;
  const plannedHours = activeShifts.reduce((sum, shift) => sum + Number(shift.planned_hours || 0), 0);
  const myAssignments = uniqueAssignments.filter((a) => {
    const shift = shiftById[a.shift_id] || {};
    return String(a.user_id) === String(currentUser?.id) && shift.status !== "cancelled" && !assignmentOfferExpired(a, shift);
  });
  const myOpenPunch = openPunches[0];
  const selectedShift = useMemo(
    () => activeShifts.find((s) => s.id === selectedShiftId) || null,
    [selectedShiftId, activeShifts]
  );

  useEffect(() => {
    if (selectedShiftId && selectedDayShifts.some((shift) => shift.id === selectedShiftId)) return;
    setSelectedShiftId(selectedDayShifts[0]?.id || null);
  }, [selectedDayShifts, selectedShiftId]);

  const publish = async (shift) => {
    try {
      await crewApi.publishShift(shift.id);
      toast.success(t("crew.toast.published"));
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const cancelShift = async (shift) => {
    const reason = window.prompt(t("crew.shift.cancel_reason"));
    if (reason === null) return;
    try {
      await crewApi.cancelShift(shift.id, reason);
      toast.success(t("crew.toast.cancelled"));
      if (String(selectedShiftId) === String(shift.id)) setSelectedShiftId(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const accept = async (assignment) => {
    try {
      await crewApi.acceptAssignment(assignment.id);
      toast.success(t("crew.toast.accepted"));
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const decline = async (assignment) => {
    try {
      await crewApi.declineAssignment(assignment.id);
      toast.success(t("crew.toast.declined"));
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const saveAvailability = async () => {
    setAvailabilitySaving(true);
    try {
      const selectedDates = availabilityForm.selectedDates || [];
      if (selectedDates.length === 0) {
        toast.error(t("crew.availability.no_days_selected"));
        return;
      }
      if (
        !isEndAfterStart(
          combineDateAndTime(selectedDates[0], availabilityForm.start_time),
          combineDateAndTime(selectedDates[0], availabilityForm.end_time)
        )
      ) {
        toast.error(t("crew.shift.ends_after_start"));
        return;
      }
      await Promise.all(
        selectedDates.map((d) =>
          crewApi.createAvailability({
            starts_at: combineDateAndTime(d, availabilityForm.start_time),
            ends_at: combineDateAndTime(d, availabilityForm.end_time),
            available: availabilityForm.available,
          })
        )
      );
      toast.success(t("crew.toast.availability_saved"));
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    } finally {
      setAvailabilitySaving(false);
    }
  };

  const toggleAvailabilityMonth = (month) => {
    setExpandedAvailabilityMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  };

  const removeAvailabilityDate = (dateToRemove) => {
    setAvailabilityForm((prev) => ({
      ...prev,
      selectedDates: (prev.selectedDates || []).filter((d) => d !== dateToRemove),
    }));
  };

  const toggleAvailabilityDate = (day) => {
    setAvailabilityForm((prev) => {
      const selected = prev.selectedDates || [];
      return {
        ...prev,
        selectedDates: selected.includes(day) ? selected.filter((d) => d !== day) : [...selected, day].sort(),
      };
    });
  };

  const addVisibleWeekdaysToAvailability = () => {
    const base = new Date(`${date}T00:00:00`);
    const monday = new Date(base);
    const day = monday.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    monday.setDate(monday.getDate() + diff);
    const days = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return formatDateInputFromDate(d);
    });
    setAvailabilityForm((prev) => ({
      ...prev,
      selectedDates: Array.from(new Set([...(prev.selectedDates || []), ...days])).sort(),
    }));
  };

  const availStart = useMemo(() => {
    const [h, m] = (availabilityForm.start_time || "08:00").split(":").map(Number);
    return h + (m || 0) / 60;
  }, [availabilityForm.start_time]);
  const availEnd = useMemo(() => {
    const [h, m] = (availabilityForm.end_time || "16:00").split(":").map(Number);
    const v = h + (m || 0) / 60;
    return v <= availStart ? Math.min(24, availStart + 1) : v;
  }, [availabilityForm.end_time, availStart]);

  const clockIn = async (shift) => {
    try {
      await crewApi.clockIn({
        shift_id: shift?.id,
        project_id: shift?.project_id || selectedProjectId,
        clock_in_at: new Date().toISOString(),
        source: "web",
      });
      toast.success(t("crew.toast.clocked_in"));
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const clockOut = async (punch) => {
    try {
      const res = await crewApi.clockOut(punch.id, {
        clock_out_at: new Date().toISOString(),
        break_minutes: punch.break_minutes || 0,
        deviation_note: punchDeviationNote || undefined,
      });
      if (res.data?.hour_error) toast.error(res.data.hour_error);
      else toast.success(t("crew.toast.clocked_out"));
      setPunchDeviationNote("");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e.message);
    }
  };

  const seedChecklists = async () => {
    try {
      await crewApi.seedChecklistTemplates();
      toast.success(t("crew.toast.checklists_seeded"));
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const completeChecklistRun = async (run) => {
    try {
      await crewApi.updateShiftChecklist(run.id, { status: "completed" });
      toast.success(t("crew.toast.checklist_completed"));
      await loadAll();
      await loadLeaderData();
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e?.response?.data?.error || e.message);
    }
  };

  const savePin = async (clear = false) => {
    if (!pinUser?.id) return;
    try {
      await crewApi.setKioskPinByUser({
        user_id: pinUser.id,
        pin: clear ? undefined : pinValue,
        clear,
      });
      toast.success(t(clear ? "crew.toast.pin_cleared" : "crew.toast.pin_saved"));
      setPinValue("");
    } catch (e) {
      toast.error(e?.response?.data?.errors?.join(", ") || e?.response?.data?.error || e.message);
    }
  };

  const tabOptions = [
    { value: "shifts", label: t("crew.tabs.shifts") },
    { value: "mine", label: t("crew.tabs.mine") },
    { value: "availability", label: t("crew.tabs.availability") },
    { value: "punch", label: t("crew.tabs.punch") },
    { value: "kiosk", label: t("crew.tabs.kiosk") },
    { value: "leader", label: t("crew.tabs.leader") },
  ];

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    if (nextTab === "availability") {
      const range = monthRange(date.substring(0, 7));
      const monthFrom = range.from.substring(0, 10);
      const monthTo = range.to.substring(0, 10);
      setPeriodType("month");
      setPeriod({ from: monthFrom, to: monthTo });
      setAvailabilityForm((prev) => ({
        ...prev,
        month: monthFrom.substring(0, 7),
      }));
      return;
    }

    const week = weekDaysFor(date);
    setPeriodType("week");
    setPeriod({ from: week[0].date, to: week[week.length - 1].date });
  };

  const handlePeriodChange = ({ periodType: nextType, from, to }) => {
    setPeriodType(nextType || "custom");
    setPeriod({ from, to });
    if ((nextType || periodType) === "month") {
      setAvailabilityForm((prev) => ({
        ...prev,
        month: from.substring(0, 7),
      }));
    }
    if (date < from || date > to) setDate(from);
  };

  return (
    <>
      <style>{SCOPE_STYLE}</style>
      <div className="crew-scope min-h-screen" style={{ background: "var(--crew-bg)" }}>
        <header className="border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
          <div className="mx-auto flex max-w-none flex-col gap-4 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                {t("crew.page.kicker")}
              </div>
              <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
                {t("crew.page.title")}
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-slate-500">
                {t("crew.page.subtitle")}
              </p>
              <div className="mt-3">
                <PlanningQuickViewTabs />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {loading && <span className="text-xs font-medium text-slate-500">{t("crew.panel.loading")}</span>}
              <Button onClick={loadAll}><ArrowPathIcon className="h-4 w-4" />{t("Refresh")}</Button>
              <Button variant="primary" onClick={() => setShiftDialog({ open: true, shift: null })}>
                <PlusIcon className="h-4 w-4" />{t("crew.shift.new_short")}
              </Button>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-none space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <StatTile icon={CalendarDaysIcon} label={t("crew.stats.shifts")} value={activeShifts.length} />
            <StatTile icon={UserGroupIcon} label={t("crew.stats.assignments")} value={uniqueAssignments.length} tone="terracotta" />
            <StatTile icon={ClockIcon} label={t("crew.stats.open_punches")} value={openPunches.length} tone="sage" />
            <StatTile icon={ExclamationTriangleIcon} label={t("crew.stats.alerts")} value={alerts.length} tone="warn" />
          </div>

          <Card className="overflow-hidden">
            <div className="border-b border-slate-200 bg-white px-5 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-950">{t("crew.filters.title")}</h2>
                  <p className="mt-1 text-xs text-slate-500">{t("crew.filters.subtitle")}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 p-5 xl:grid-cols-[minmax(260px,1.2fr)_minmax(360px,1.6fr)_minmax(180px,0.8fr)]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("crew.filters.project")}
                </div>
                <ProjectSelector label={false} selected={project} onChange={(e) => setProject(e.target.object)} />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-1">
                <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {t("crew.filters.period")}
                    </div>
                    <div className="crew-tnum text-xs font-medium text-slate-600">
                      {normalizedPeriod.from} - {normalizedPeriod.to}
                    </div>
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                    {scheduleDays.length} {t("crew.filters.days")}
                  </span>
                </div>
                <PeriodSelector
                  periodType={periodType}
                  baseDate={date}
                  from={normalizedPeriod.from}
                  to={normalizedPeriod.to}
                  onChange={handlePeriodChange}
                  allowedPeriodTypes={activeTab === "availability" ? ["month"] : ["week"]}
                />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <Field label={t("crew.filters.active_day")}>
                  <input
                    type="date"
                    className={inputClass}
                    style={inputStyle}
                    min={visibleScheduleRange.from}
                    max={visibleScheduleRange.to}
                    value={date}
                    onChange={(e) => {
                      if (e.target.value) setDate(e.target.value);
                    }}
                  />
                </Field>
                <div className="mt-4">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("crew.filters.density")}
                  </div>
                  <Segmented
                    options={[
                      { value: "cozy", label: t("crew.density.cozy", { defaultValue: "Cozy" }) },
                      { value: "compact", label: t("crew.density.compact", { defaultValue: "Compact" }) },
                    ]}
                    value={density}
                    onChange={setDensity}
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </Card>

          <div className="flex flex-wrap items-center gap-3">
            <Segmented options={tabOptions} value={activeTab} onChange={handleTabChange} />
            {loading && <span className="text-xs" style={{ color: "var(--crew-ink-3)" }}>{t("crew.panel.loading")}</span>}
          </div>

          {activeTab === "shifts" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-2xl p-4" style={{ background: "var(--crew-paper)", border: "1px solid var(--crew-line)" }}>
                  <p className="text-[10px] font-medium uppercase tracking-[.08em]" style={{ color: "var(--crew-ink-3)" }}>{t("crew.shift.overview_period")}</p>
                  <p className="crew-tnum mt-1 text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{normalizedPeriod.from} - {normalizedPeriod.to}</p>
                </div>
                <div className="rounded-2xl p-4" style={{ background: "var(--crew-sage-soft)", border: "1px solid #BFDBFE" }}>
                  <p className="text-[10px] font-medium uppercase tracking-[.08em]" style={{ color: "#1D4ED8" }}>{t("crew.shift.days_with_shifts")}</p>
                  <p className="crew-tnum mt-1 text-xl font-semibold" style={{ color: "#1D4ED8" }}>
                    {plannedDays.length} <span style={{ color: "var(--crew-ink-3)", fontWeight: 500 }}>/ {scheduleDays.length}</span>
                  </p>
                </div>
                <div className="rounded-2xl p-4" style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                  <p className="text-[10px] font-medium uppercase tracking-[.08em]" style={{ color: "#92400E" }}>{t("crew.shift.missing_days")}</p>
                  <p className="crew-tnum mt-1 text-xl font-semibold" style={{ color: "#92400E" }}>{missingDays}</p>
                </div>
                <div className="rounded-2xl p-4" style={{ background: "var(--crew-terracotta-soft)", border: "1px solid var(--crew-line)" }}>
                  <p className="text-[10px] font-medium uppercase tracking-[.08em]" style={{ color: "var(--crew-terracotta-ink)" }}>{t("crew.shift.planned_hours")}</p>
                  <p className="crew-tnum mt-1 text-xl font-semibold" style={{ color: "var(--crew-terracotta-ink)" }}>{plannedHours.toFixed(1)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_360px]">
                <WeekTimeline
                  shifts={activeShifts}
                  weekDays={weekDays}
                  dayIdx={dayIdx}
                  setDayIdx={(i) => setDate(weekDays[i].date)}
                  date={date}
                  setDate={setDate}
                  onSelectShift={(s) => setSelectedShiftId(s.id)}
                  selectedShiftId={selectedShiftId}
                  shiftsByDate={shiftsByDate}
                  density={density}
                />
                <ShiftDetailPanel
                  shift={selectedShift}
                  onEdit={() => selectedShift && setShiftDialog({ open: true, shift: selectedShift })}
                  onCopy={() => selectedShift && setShiftDialog({ open: true, shift: null, copyFrom: selectedShift })}
                  onAssign={() => selectedShift && setAssignmentDialog({ open: true, shift: selectedShift })}
                  onPublish={() => selectedShift && publish(selectedShift)}
                  onCancel={() => selectedShift && cancelShift(selectedShift)}
                />
              </div>

              <Card className="overflow-hidden">
                <div className="px-5 py-4" style={{ background: "var(--crew-paper-2)", borderBottom: "1px solid var(--crew-line)" }}>
                  <h3 className="text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.shift.day_by_day")}</h3>
                </div>
                <div className="divide-y" style={{ borderColor: "var(--crew-line-2)" }}>
                  {scheduleDays
                    .filter((day) => (shiftsByDate.get(day.date) || []).length > 0)
                    .map((day) => (
                      <div key={day.date} className="p-5">
                        <div className="mb-3 flex items-center justify-between">
                          <h4 className="crew-tnum text-base font-semibold" style={{ color: "var(--crew-ink)" }}>{day.date}</h4>
                          <Pill>{t("crew.shift.day_shift_count", { count: (shiftsByDate.get(day.date) || []).length })}</Pill>
                        </div>
                        <div className="space-y-3">
                          {(shiftsByDate.get(day.date) || []).map((shift) => (
                            <div
                              key={shift.id}
                              className="grid grid-cols-1 gap-3 rounded-xl p-3 lg:grid-cols-[1fr_auto]"
                              style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line-2)" }}
                            >
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="font-semibold" style={{ color: "var(--crew-ink)" }}>{shift.title}</h3>
                                  <Pill>{shift.status}</Pill>
                                  <Pill tone="terracotta">{shift.assignment_mode}</Pill>
                                </div>
                                <p className="mt-1 text-sm" style={{ color: "var(--crew-ink-2)" }}>
                                  <span className="crew-mono">
                                    {formatLocalDateTimeInput(shift.starts_at).replace("T", " ")} – {formatLocalDateTimeInput(shift.ends_at).replace("T", " ")}
                                  </span>
                                  {shift.location ? ` · ${shift.location}` : ""}
                                  {shift.role_label ? ` · ${shift.role_label}` : ""}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                                  {dedupeAssignments(shift.assignments || []).map((a) => {
                                    const isMine = String(a.user_id) === String(currentUser?.id);
                                    return (
                                      <Pill key={a.id} tone={isMine ? "sage" : "neutral"}>
                                        {(a.user && (a.user.displayTitle || a.user.email)) || a.user_id || "open"} · {a.status}
                                      </Pill>
                                    );
                                  })}
                                  {dedupeAssignments(shift.assignments || []).length === 0 && (
                                    <Pill tone="warn">{t("crew.shift.no_crew_added")}</Pill>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-start gap-2 lg:justify-end">
                                <Button onClick={() => setShiftDialog({ open: true, shift })}>{t("Edit")}</Button>
                                <Button onClick={() => setShiftDialog({ open: true, shift: null, copyFrom: shift })}>{t("crew.shift.copy_short")}</Button>
                                <Button onClick={() => setAssignmentDialog({ open: true, shift })}>{t("crew.assignment.add_short")}</Button>
                                {shift.status !== "published" && (<Button variant="accent" onClick={() => publish(shift)}>{t("crew.shift.publish")}</Button>)}
                                {shift.status !== "cancelled" && (<Button variant="danger" onClick={() => cancelShift(shift)}>{t("crew.shift.cancel")}</Button>)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  {activeShifts.length === 0 && (<p className="p-6 text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.empty.shifts")}</p>)}
                </div>
              </Card>
            </div>
          )}

          {activeTab === "mine" && (
            <Card className="p-5">
              <h2 className="mb-3 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.tabs.mine")}</h2>
              <div className="space-y-3">
                {myAssignments.map((a) => {
                  const shift = shiftById[a.shift_id] || {};
                  const canRespond = String(a.user_id) === String(currentUser?.id) && a.status === "offered";
                  return (
                    <div key={a.id} className="rounded-xl p-3" style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line-2)" }}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar label={shift.title || "Shift"} size={36} />
                          <div>
                            <div className="font-medium" style={{ color: "var(--crew-ink)" }}>{shift.title || `#${a.shift_id}`}</div>
                            <div className="text-sm" style={{ color: "var(--crew-ink-3)" }}>
                              {shift.starts_at ? (<span className="crew-mono">{formatLocalDateTimeInput(shift.starts_at).replace("T", " ")}</span>) : null}
                              {" · "}
                              <Pill tone={a.status === "accepted" ? "sage" : "neutral"}>{a.status}</Pill>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="accent" onClick={() => accept(a)} disabled={!canRespond}><CheckCircleIcon className="h-4 w-4" />{t("crew.assignment.accept")}</Button>
                          <Button variant="danger" onClick={() => decline(a)} disabled={!canRespond}><XCircleIcon className="h-4 w-4" />{t("crew.assignment.decline")}</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {myAssignments.length === 0 && (<p className="text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.empty.assignments")}</p>)}
              </div>
            </Card>
          )}

          {activeTab === "availability" && (
            <Card className="p-5">
              <h2 className="mb-3 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.tabs.availability")}</h2>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.8fr_1fr]">
                <div className="rounded-2xl p-4" style={{ background: "var(--crew-paper)", border: "1px solid var(--crew-line)" }}>
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--crew-ink-3)" }}>
                        {t("crew.availability.month")}
                      </div>
                      <div className="crew-tnum mt-1 text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>
                        {availabilityMonth}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={addVisibleWeekdaysToAvailability}>{t("crew.availability.add_weekdays")}</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium" style={{ color: "var(--crew-ink-3)" }}>
                    {weekdayOptions.map((d) => (<div key={d.value} className="py-1">{t(d.key)}</div>))}
                  </div>
                  <div className="mt-1 grid grid-cols-7 gap-1">
                    {monthCalendarDays(availabilityMonth).map((day) => {
                      const selected = (availabilityForm.selectedDates || []).includes(day.date);
                      const existing = availabilityItems.some((a) => String(a.starts_at || "").startsWith(day.date));
                      return (
                        <button
                          key={day.date}
                          type="button"
                          onClick={() => toggleAvailabilityDate(day.date)}
                          className="min-h-[68px] rounded-xl p-2 text-left transition sm:min-h-[88px]"
                          style={{
                            border: `1px solid ${selected ? "var(--crew-sage)" : existing ? "#BFE0DA" : "var(--crew-line-2)"}`,
                            background: selected ? "var(--crew-sage-soft)" : existing ? "var(--crew-sage-soft)" : "var(--crew-paper)",
                            color: selected || existing ? "#1F7A6F" : "var(--crew-ink-2)",
                            opacity: day.currentMonth ? 1 : 0.4,
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <span className="crew-tnum text-sm font-semibold">{day.day}</span>
                            {selected && (<span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: "var(--crew-sage)" }}>✓</span>)}
                          </div>
                          <div className="mt-2 text-[11px] leading-tight" style={{ color: "var(--crew-ink-3)" }}>
                            {existing ? t("crew.availability.saved_short") : selected ? t("crew.availability.selected_short") : ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl p-4" style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line)" }}>
                    <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[.08em]" style={{ color: "var(--crew-ink-3)" }}>{t("crew.availability.selected_days")}</h3>
                    <div className="flex flex-wrap gap-2">
                      {(availabilityForm.selectedDates || []).map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => removeAvailabilityDate(d)}
                          className="rounded-full px-3 py-1 text-xs font-medium"
                          style={{ background: "#fff", border: "1px solid var(--crew-line)", color: "var(--crew-ink-2)" }}
                          title={t("crew.availability.remove_day")}
                        >
                          {d} ×
                        </button>
                      ))}
                      {(availabilityForm.selectedDates || []).length === 0 && (
                        <span className="text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.availability.no_days_selected")}</span>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl p-4 space-y-4" style={{ background: "var(--crew-paper)", border: "1px solid var(--crew-line)" }}>
                    <RangeTimeSlider
                      start={availStart}
                      end={availEnd}
                      color={availabilityForm.available ? "var(--crew-sage)" : "var(--crew-rose)"}
                      onChange={(s, e) => setAvailabilityForm((p) => ({ ...p, start_time: fmtHM(s), end_time: fmtHM(e) }))}
                    />
                    <Field label={t("crew.availability.status")}>
                      <Segmented
                        options={[
                          { value: "true", label: t("crew.availability.available") },
                          { value: "false", label: t("crew.availability.unavailable") },
                        ]}
                        value={availabilityForm.available ? "true" : "false"}
                        onChange={(v) => setAvailabilityForm((p) => ({ ...p, available: v === "true" }))}
                        size="sm"
                      />
                    </Field>
                    <Button variant="primary" className="w-full" onClick={saveAvailability} disabled={availabilitySaving}>
                      {availabilitySaving ? t("Saving") : t("Save")}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl" style={{ border: "1px solid var(--crew-line)" }}>
                <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--crew-paper-2)" }}>
                  <h3 className="text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.availability.monthly_overview")}</h3>
                  {(loading || availabilitySaving) && (<span className="text-xs" style={{ color: "var(--crew-ink-3)" }}>{t("crew.availability.loading")}</span>)}
                </div>
                <div className="divide-y" style={{ borderColor: "var(--crew-line-2)" }}>
                  {availabilityGroups.map((group) => {
                    const open = expandedAvailabilityMonths.includes(group.month) || group.month === availabilityMonth;
                    return (
                      <div key={group.month}>
                        <button
                          type="button"
                          className="flex w-full flex-col gap-1 px-4 py-3 text-left transition sm:flex-row sm:items-center sm:justify-between"
                          onClick={() => toggleAvailabilityMonth(group.month)}
                          style={{ background: "transparent" }}
                        >
                          <span className="font-medium crew-tnum" style={{ color: "var(--crew-ink)" }}>{group.month}</span>
                          <span className="text-sm" style={{ color: "var(--crew-ink-3)" }}>
                            {t("crew.availability.month_stats", { days: group.dayCount, hours: group.hours.toFixed(2) })}
                          </span>
                        </button>
                        {open && (
                          <div className="divide-y" style={{ background: "#fff", borderColor: "var(--crew-line-2)" }}>
                            {group.items.map((a) => (
                              <div key={a.id} className="flex items-center justify-between px-4 py-3 text-sm">
                                <span className="crew-mono" style={{ color: "var(--crew-ink-2)" }}>
                                  {formatLocalDateTimeInput(a.starts_at).replace("T", " ")} – {formatLocalDateTimeInput(a.ends_at).replace("T", " ")}
                                </span>
                                <Pill tone={a.available ? "sage" : "rose"}>
                                  {a.available ? t("crew.availability.available") : t("crew.availability.unavailable")}
                                </Pill>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {availabilityGroups.length === 0 && (<p className="px-4 py-4 text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.availability.no_entries")}</p>)}
                </div>
              </div>
            </Card>
          )}

          {activeTab === "punch" && (
            <Card className="p-5">
              <h2 className="mb-3 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.tabs.punch")}</h2>
              {myOpenPunch ? (
                <div className="rounded-xl p-4" style={{ background: "var(--crew-sage-soft)", color: "#1F7A6F" }}>
                  <p className="font-medium">{t("crew.punch.clocked_in")}</p>
                  <p className="crew-mono mt-1 text-sm">{myOpenPunch.clock_in_at}</p>
                  <textarea
                    className={`${inputClass} mt-3`}
                    style={{ ...inputStyle, minHeight: 82 }}
                    placeholder={t("crew.punch.deviation_note_placeholder")}
                    value={punchDeviationNote}
                    onChange={(e) => setPunchDeviationNote(e.target.value)}
                  />
                  <Button className="mt-3" variant="primary" onClick={() => clockOut(myOpenPunch)}>{t("crew.panel.clock_out")}</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm" style={{ color: "var(--crew-ink-2)" }}>{t("crew.punch.choose_shift")}</p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {activeShifts.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => clockIn(s)}
                        className="rounded-xl p-4 text-left transition"
                        style={{ background: "#fff", border: "1px solid var(--crew-line)" }}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar label={s.title} size={36} />
                          <div>
                            <div className="font-medium" style={{ color: "var(--crew-ink)" }}>{s.title}</div>
                            <div className="crew-mono text-sm" style={{ color: "var(--crew-ink-3)" }}>{formatLocalDateTimeInput(s.starts_at).replace("T", " ")}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <Button onClick={() => clockIn(null)}>{t("crew.punch.clock_in_without_shift")}</Button>
                </div>
              )}
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.punch.history")}</h3>
                  <Button onClick={loadPunchHistory}>{t("Refresh")}</Button>
                </div>
                <div className="overflow-hidden rounded-2xl" style={{ border: "1px solid var(--crew-line)" }}>
                  <div className="hidden grid-cols-6 gap-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-[.08em] md:grid" style={{ background: "var(--crew-paper-2)", color: "var(--crew-ink-3)" }}>
                    <span>{t("crew.punch.history_date")}</span>
                    <span>{t("crew.punch.history_in")}</span>
                    <span>{t("crew.punch.history_out")}</span>
                    <span>{t("crew.punch.history_hours")}</span>
                    <span>{t("crew.punch.history_status")}</span>
                    <span>{t("crew.punch.deviation_note")}</span>
                  </div>
                  <div className="divide-y" style={{ borderColor: "var(--crew-line-2)" }}>
                    {punchHistory.map((p) => (
                      <div key={p.id} className="grid grid-cols-1 gap-1 px-4 py-3 text-sm md:grid-cols-6 md:gap-2" style={{ color: "var(--crew-ink-2)" }}>
                        <span className="crew-tnum font-medium" style={{ color: "var(--crew-ink)" }}>{String(p.clock_in_at || "").slice(0, 10)}</span>
                        <span className="crew-mono">{String(p.clock_in_at || "").replace("T", " ").slice(11, 16)}</span>
                        <span className="crew-mono">{p.clock_out_at ? String(p.clock_out_at).replace("T", " ").slice(11, 16) : "—"}</span>
                        <span className="crew-tnum">{p.actual_hours != null ? Number(p.actual_hours).toFixed(2) : "—"}</span>
                        <span><Pill tone={p.clock_out_at ? "neutral" : "sage"}>{p.clock_out_at ? t("crew.punch.closed") : t("crew.punch.open")}</Pill></span>
                        <span className="text-xs" style={{ color: p.deviation_note ? "var(--crew-rose)" : "var(--crew-ink-3)" }}>{p.deviation_note || "—"}</span>
                      </div>
                    ))}
                    {punchHistory.length === 0 && (<p className="px-4 py-4 text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.punch.no_history")}</p>)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === "kiosk" && (
            <Card className="p-5">
              <h2 className="mb-3 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.tabs.kiosk")}</h2>
              <p className="mb-4 text-sm" style={{ color: "var(--crew-ink-2)" }}>{t("crew.kiosk.help")}</p>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-2xl p-4" style={{ border: "1px solid var(--crew-line)", background: "var(--crew-paper-2)" }}>
                  <h3 className="mb-3 text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.kiosk.verify_title")}</h3>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="primary" onClick={() => setKioskOpen(true)}><KeyIcon className="h-4 w-4" />{t("crew.kiosk.open")}</Button>
                    <Button onClick={seedChecklists}>{t("crew.panel.seed_checklists")}</Button>
                  </div>
                </div>
                <div className="rounded-2xl p-4" style={{ border: "1px solid var(--crew-line)", background: "var(--crew-paper-2)" }}>
                  <h3 className="mb-3 text-sm font-semibold" style={{ color: "var(--crew-ink)" }}>{t("crew.kiosk.pin_admin_title")}</h3>
                  <div className="space-y-3">
                    <EmployeeSelector selected={pinUser} onChange={(e) => setPinUser(e.target.object)} />
                    <input
                      type="password"
                      inputMode="numeric"
                      className={inputClass}
                      style={inputStyle}
                      placeholder={t("crew.kiosk.pin")}
                      value={pinValue}
                      onChange={(e) => setPinValue(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button variant="primary" onClick={() => savePin(false)} disabled={!pinUser?.id || !pinValue}>{t("crew.kiosk.save_pin")}</Button>
                      <Button variant="danger" onClick={() => savePin(true)} disabled={!pinUser?.id}>{t("crew.kiosk.clear_pin")}</Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === "leader" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <Card className="p-5">
                <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>
                  <ExclamationTriangleIcon className="h-5 w-5" style={{ color: "var(--crew-warn)" }} />
                  {t("crew.tabs.leader_alerts")}
                </h2>
                <div className="space-y-2">
                  {alerts.map((a, idx) => (
                    <div key={idx} className="rounded-xl p-3 text-sm" style={{ background: "#FBEFC4", color: "#8A6800" }}>
                      <div className="font-semibold">{t(`crew.alerts.${a.kind}`, { defaultValue: a.kind })}</div>
                      {a.deviation_note && <div className="mt-1 text-xs">{a.deviation_note}</div>}
                      {a.checklist_kind && <div className="mt-1 text-xs">{t(`crew.checklists.kind.${a.checklist_kind}`)} · {a.status}</div>}
                    </div>
                  ))}
                  {alerts.length === 0 && (<p className="text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.empty.alerts")}</p>)}
                </div>
              </Card>
              <Card className="p-5">
                <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>
                  <CheckCircleIcon className="h-5 w-5" style={{ color: "var(--crew-sage)" }} />
                  {t("crew.checklists.handover_title")}
                </h2>
                <div className="space-y-3">
                  {checklistRuns.map((run) => (
                    <div key={run.id} className="rounded-xl p-3 text-sm" style={{ background: "var(--crew-paper-2)", border: "1px solid var(--crew-line)" }}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold" style={{ color: "var(--crew-ink)" }}>
                            {run.shift?.title || `#${run.shift_id}`} · {t(`crew.checklists.kind.${run.kind}`)}
                          </div>
                          <div className="crew-mono mt-1 text-xs" style={{ color: "var(--crew-ink-3)" }}>
                            {run.shift?.starts_at ? formatLocalDateTimeInput(run.shift.starts_at).replace("T", " ") : ""}
                          </div>
                        </div>
                        <Pill tone={run.status === "completed" ? "sage" : "warn"}>{t(`crew.checklists.status.${run.status}`, { defaultValue: run.status })}</Pill>
                      </div>
                      {run.note && <p className="mt-2 text-xs" style={{ color: "var(--crew-ink-2)" }}>{run.note}</p>}
                      {run.deviation_summary && <p className="mt-2 text-xs font-semibold" style={{ color: "var(--crew-rose)" }}>{run.deviation_summary}</p>}
                      {run.checklist?.items?.some((item) => item.isDeviation) && (
                        <div className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">
                          {run.checklist.items.filter((item) => item.isDeviation).map((item) => item.title).join(", ")}
                        </div>
                      )}
                      {run.status !== "completed" && (
                        <Button className="mt-3" onClick={() => completeChecklistRun(run)}>{t("crew.checklists.mark_completed")}</Button>
                      )}
                    </div>
                  ))}
                  {checklistRuns.length === 0 && (<p className="text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.checklists.no_runs")}</p>)}
                </div>
              </Card>
              <Card className="p-5">
                <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold" style={{ color: "var(--crew-ink)" }}>
                  <BanknotesIcon className="h-5 w-5" style={{ color: "var(--crew-sage)" }} />
                  {t("crew.tabs.payroll")}
                </h2>
                <div className="overflow-hidden rounded-xl" style={{ border: "1px solid var(--crew-line)" }}>
                  <div className="divide-y" style={{ borderColor: "var(--crew-line-2)" }}>
                    {(payroll?.rows || []).map((r) => (
                      <div key={r.user_id} className="grid grid-cols-3 gap-2 p-3 text-sm" style={{ color: "var(--crew-ink-2)" }}>
                        <span className="font-medium">User {r.user_id}</span>
                        <span>{t("crew.payroll.planned")}: <span className="crew-tnum">{Number(r.planned_hours || 0).toFixed(2)}</span></span>
                        <span>{t("crew.payroll.actual")}: <span className="crew-tnum">{Number(r.actual_hours || 0).toFixed(2)}</span></span>
                      </div>
                    ))}
                    {(!payroll?.rows || payroll.rows.length === 0) && (<p className="p-3 text-sm" style={{ color: "var(--crew-ink-3)" }}>{t("crew.empty.payroll")}</p>)}
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
        </div>

      <ShiftDialog
        open={shiftDialog.open}
        shift={shiftDialog.shift}
        copyFrom={shiftDialog.copyFrom}
        project={project || (selectedProjectId ? { id: selectedProjectId } : null)}
        onClose={() => setShiftDialog({ open: false, shift: null, copyFrom: null })}
        onSaved={loadAll}
      />
      <AssignmentDialog
        open={assignmentDialog.open}
        shift={assignmentDialog.shift}
        onClose={() => setAssignmentDialog({ open: false, shift: null })}
        onSaved={loadAll}
      />
      <KioskDialog open={kioskOpen} onClose={() => setKioskOpen(false)} />
    </>
  );
}

export function CrewScheduling() {
  const canAccess = useUserHasAccess({
    roles: ["projects_plan", "hours", "edit_hours", "hours_approval"],
    feature: "crew_scheduling",
  });
  if (!canAccess) return <Navigate to="/dashboard" replace />;
  return <CrewSchedulingContent />;
}

export default CrewScheduling;
